import React, { useEffect, useState } from 'react';
import { runBackground } from '../host/runBackground.js';
import * as m from '../paraglide/messages.js';
import type { PromptActivityEntry } from './api.js';
import { fetchPromptActivity } from './api.js';
import { CloseIcon } from './icons.js';

const POLL_MS = 5_000;

export type PromptActivityPanelProps = {
  readonly onClose: () => void;
  readonly onUnauthorized: () => void;
};

function formatPromptTime(at: number): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
    }).format(new Date(at));
  } catch {
    return new Date(at).toISOString();
  }
}

export function PromptActivityPanel(props: PromptActivityPanelProps): React.ReactElement {
  const [entries, setEntries] = useState<readonly PromptActivityEntry[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);

  const { onUnauthorized } = props;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function refresh(): Promise<void> {
      try {
        const view = await fetchPromptActivity(controller.signal);
        if (cancelled) {
          return;
        }
        setEntries(view.entries);
        setError(undefined);
      } catch (cause) {
        if (cancelled) {
          return;
        }
        if (cause instanceof Error && cause.name === 'AbortError') {
          return;
        }
        if (
          cause !== null &&
          typeof cause === 'object' &&
          'status' in cause &&
          (cause as { readonly status?: number }).status === 401
        ) {
          onUnauthorized();
          return;
        }
        setError(m.promptActivityLoadFailed());
      }
    }

    runBackground(refresh());
    const timer = window.setInterval(() => {
      runBackground(refresh());
    }, POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [onUnauthorized]);

  return (
    <aside className="studio-prompt-activity" aria-labelledby="studio-prompt-activity-title">
      <div className="studio-prompt-activity-header">
        <h2 id="studio-prompt-activity-title">{m.promptActivityTitle()}</h2>
        <button
          type="button"
          className="icon-button"
          onClick={props.onClose}
          aria-label={m.promptActivityClose()}
          title={m.promptActivityClose()}
        >
          <CloseIcon />
        </button>
      </div>
      <p className="studio-prompt-activity-lead">{m.promptActivityLead()}</p>
      {error !== undefined ? <p className="studio-prompt-activity-error">{error}</p> : undefined}
      {entries.length === 0 && error === undefined ? (
        <p className="studio-prompt-activity-empty">{m.promptActivityEmpty()}</p>
      ) : (
        <ul className="studio-prompt-activity-list">
          {entries.map((entry) => (
            <li key={entry.id} className="studio-prompt-activity-item">
              <div className="studio-prompt-activity-meta">
                <time dateTime={new Date(entry.at).toISOString()}>
                  {formatPromptTime(entry.at)}
                </time>
                <span className="studio-prompt-activity-email">{entry.email}</span>
              </div>
              <div className="studio-prompt-activity-session">
                <span className="studio-prompt-activity-target">{entry.targetLabel}</span>
                <span className="studio-prompt-activity-title-text">
                  {entry.sessionTitle.trim() === ''
                    ? m.promptActivityUntitled()
                    : entry.sessionTitle}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
